import React, { useState, useEffect } from "react";
import { Input, Modal, Spin } from "antd";
import { useDispatch } from "react-redux";

import { fetchData, profileData } from "../../../slices/profileSlice";
import { useNavigate } from "react-router-dom";
import TopNav from "../../../components/top-nav";
import FooterDisplay from "../../../components/footer";
import {
  _apply_as_facilitator,
  _get_profile,
} from "../../../common/axios_services";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../../../common/utils/notification";
import AppRoutes from "../../../common/routes";
import {
  cleanPhoneForInput,
  validatePhoneNumber,
  formatFullPhone,
} from "../../../utils/phoneHelpers";

const JoinAsFacilitator = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [phoneCode, setPhoneCode] = useState("+234");
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [hasExperience, setHasExperience] = useState(null);
  const [onboardRange, setOnboardRange] = useState(null);
  const [hasTimeDaily, setHasTimeDaily] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [activeTermsTab, setActiveTermsTab] = useState("overview");

  const validateWhatsappNumber = () => {
    if (!whatsappNumber || !whatsappNumber.trim()) {
      showErrorNotification("Please enter a valid WhatsApp number.");
      return false;
    }
    if (!validatePhoneNumber(whatsappNumber)) {
      showErrorNotification("Please enter a valid WhatsApp number (7 to 15 digits).");
      return false;
    }
    return true;
  };

  const fetchUserProfile = async () => {
    try {
      const profileResponse = await _get_profile();
      const user = profileResponse.data.data;
      setUserProfile(user);
      if (user && !user.isFacilitator) {
        const code = user.phoneCode || "+234";
        setPhoneCode(code);
        const rawPhone = user.facilitatorWhatsapp || user.phoneNumber || "";
        setWhatsappNumber(cleanPhoneForInput(rawPhone, code));
      }
    } catch (err) {
      console.error("Error fetching user profile:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const handleApply = async () => {
    if (!validateWhatsappNumber()) return;
    if (hasExperience === null) {
      showErrorNotification("Please answer if you have experience onboarding restaurants/stores.");
      return;
    }
    if (onboardRange === null) {
      showErrorNotification("Please select how many businesses you can onboard monthly.");
      return;
    }
    if (hasTimeDaily === null) {
      showErrorNotification("Please answer if you have time to help businesses set up.");
      return;
    }
    if (!agreedToTerms) {
      showErrorNotification("You must read and agree to the Facilitator agreements & terms.");
      return;
    }

    setSubmitting(true);
    try {
      const formattedWhatsapp = formatFullPhone(phoneCode, whatsappNumber);
      const res = await _apply_as_facilitator({ whatsappNumber: formattedWhatsapp });
      if (res.data?.success || res.status === 200 || res.status === 201) {
        showSuccessNotification("Successfully applied to become a Facilitator!");
        dispatch(profileData(false));
        dispatch(fetchData(false));
        setShowSuccessModal(true);
      } else {
        showErrorNotification(res.data?.message || "Failed to process application");
      }
    } catch (err) {
      showErrorNotification(
        err.response?.data?.message || err.message || "Failed to process application"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate(AppRoutes.profile_referral);
  };

  const benefits = [
    {
      icon: "🏢",
      bg: "bg-blue-100",
      title: "Business Sales Income",
      desc: "Earn 1.5% from all orders placed at businesses you onboard.",
    },
    {
      icon: "💸",
      bg: "bg-green-100",
      title: "Milestone Rewards",
      desc: "Get paid up to ₦12,000 for each business branch location you facilitate.",
    },
    {
      icon: "✅",
      bg: "bg-yellow-100",
      title: "Direct Support",
      desc: "Get full support, introduction letters, and materials from the Konnect team.",
    },
  ];

  return (
    <div className="wise9ja-content">
      <TopNav title="Konnect Facilitator" />

      <div className="pb-40">
        <div className="contain mt-5">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <Spin size="large" />
            </div>
          ) : userProfile?.isFacilitator ? (
            <div className="space-y-6">
              <div className="bg-white border-2 border-gray-100 rounded-2xl p-6 shadow-sm text-center flex flex-col items-center">
                <div className="mb-4 bg-green-50 p-4 rounded-2xl inline-block border border-green-100">
                  <span className="text-5xl">🏢</span>
                </div>
                <h2 className="text-4xl font-extrabold text-black leading-tight mb-2">
                  You are a Konnect Facilitator!
                </h2>
                <p className="text-gray-500 text-lg mb-6">
                  Thank you for helping businesses grow on Konnect.
                </p>

                <div className="w-full bg-gray-50 p-5 rounded-2xl border border-gray-100 text-left mb-6 space-y-4">
                  <div className="flex justify-between border-b border-gray-200 pb-3">
                    <span className="text-lg text-gray-500">WhatsApp</span>
                    <span className="text-lg font-extrabold text-black">{userProfile.facilitatorWhatsapp}</span>
                  </div>
                  <div className="flex justify-between pt-1">
                    <span className="text-lg text-gray-500">Approved</span>
                    <span className="text-lg font-extrabold text-black">
                      {userProfile.facilitatorApprovedAt ? new Date(userProfile.facilitatorApprovedAt).toLocaleDateString() : new Date().toLocaleDateString()}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => navigate(AppRoutes.profile_referral)}
                  className="bg-black text-white py-4 px-8 text-lg font-bold rounded-xl self-stretch hover:bg-gray-800 transition active:scale-95"
                >
                  View Referral Dashboard
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              <div className="bg-white border-2 border-gray-100 rounded-2xl p-8 shadow-sm text-center flex flex-col items-center">
                <div className="mb-4 bg-green-50 p-4 rounded-2xl inline-block border border-green-100">
                  <span className="text-4xl">🏢</span>
                </div>
                <h1 className="text-4xl font-extrabold text-black leading-tight mb-3">
                  Become a Konnect Facilitator
                </h1>
                <div className="border border-green-200 rounded-xl p-4 mb-2 bg-green-50/30 w-full">
                  <p className="text-gray-900 text-lg">
                    Onboard top restaurants and stores, help them grow, and <span className="font-extrabold text-[#16a34a]">earn weekly income</span> from their sales.
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {benefits.map((b, i) => (
                  <div key={i} className="bg-white border border-gray-100 rounded-2xl p-5 flex gap-4 shadow-sm">
                    <div className={`${b.bg} p-3 rounded-xl flex-shrink-0 flex items-center justify-center w-12 h-12`}>
                      <span className="text-2xl">{b.icon}</span>
                    </div>
                    <div className="text-left">
                      <h3 className="text-lg font-extrabold text-black leading-tight mb-1">
                        {b.title}
                      </h3>
                      <p className="text-gray-500 text-base">{b.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white border-2 border-gray-100 rounded-2xl p-6 shadow-sm space-y-6">
                <h2 className="text-2xl font-extrabold text-black text-left mb-2">
                  Facilitator Application
                </h2>

                <div className="space-y-4 text-left">
                  <div>
                    <label className="text-base font-bold text-gray-700 block mb-2">
                      WhatsApp Number
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={phoneCode}
                        onChange={(e) => setPhoneCode(e.target.value)}
                        placeholder="+234"
                        style={{ width: "90px", fontSize: "16px", fontWeight: "bold" }}
                        size="large"
                      />
                      <Input
                        value={whatsappNumber}
                        onChange={(e) => setWhatsappNumber(e.target.value)}
                        placeholder="WhatsApp Number"
                        style={{ flex: 1, fontSize: "16px" }}
                        size="large"
                      />
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-100">
                    <label className="text-base font-bold text-gray-700 block mb-3">
                      1. Do you have experience onboarding restaurants or stores?
                    </label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setHasExperience(true)}
                        className={`flex-1 py-3 rounded-xl border-2 text-lg font-bold transition-all ${
                          hasExperience === true
                            ? "border-green-600 bg-green-50 text-green-700"
                            : "border-gray-200 text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        onClick={() => setHasExperience(false)}
                        className={`flex-1 py-3 rounded-xl border-2 text-lg font-bold transition-all ${
                          hasExperience === false
                            ? "border-green-600 bg-green-50 text-green-700"
                            : "border-gray-200 text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-100">
                    <label className="text-base font-bold text-gray-700 block mb-3">
                      2. How many businesses or vendors can you onboard monthly?
                    </label>
                    <div className="space-y-2">
                      {[
                        { label: "1 - 5 businesses", value: "1-5" },
                        { label: "5 & above", value: "5-above" },
                      ].map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setOnboardRange(opt.value)}
                          className={`w-full py-3 rounded-xl border-2 text-lg font-bold transition-all text-center ${
                            onboardRange === opt.value
                              ? "border-green-600 bg-green-50 text-green-700"
                              : "border-gray-200 text-gray-500 hover:border-gray-300"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-100">
                    <label className="text-base font-bold text-gray-700 block mb-3">
                      3. Do you have the time to help businesses set up daily?
                    </label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => setHasTimeDaily(true)}
                        className={`flex-1 py-3 rounded-xl border-2 text-lg font-bold transition-all ${
                          hasTimeDaily === true
                            ? "border-green-600 bg-green-50 text-green-700"
                            : "border-gray-200 text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        type="button"
                        onClick={() => setHasTimeDaily(false)}
                        className={`flex-1 py-3 rounded-xl border-2 text-lg font-bold transition-all ${
                          hasTimeDaily === false
                            ? "border-green-600 bg-green-50 text-green-700"
                            : "border-gray-200 text-gray-500 hover:border-gray-300"
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-4 flex flex-col items-center space-y-3">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTermsTab("overview");
                      setShowTermsModal(true);
                    }}
                    className="text-[#44843F] font-bold text-lg hover:underline transition-all"
                  >
                    Click to read the Facilitator agreements & terms
                  </button>

                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setActiveTermsTab("overview");
                          setShowTermsModal(true);
                        } else {
                          setAgreedToTerms(false);
                        }
                      }}
                      className="w-6 h-6 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
                    />
                    <span className="text-gray-700 text-lg font-bold">
                      I agree to the Facilitator agreements & terms
                    </span>
                  </label>
                </div>

                <button
                  onClick={handleApply}
                  disabled={submitting}
                  className={`uppercase text-white flex items-center justify-center rounded-xl font-extrabold w-full transition-all py-5 px-6 shadow-lg ${
                    submitting || !agreedToTerms
                      ? "bg-gray-300 cursor-not-allowed shadow-none"
                      : "bg-green-600 hover:bg-green-700 hover:shadow-xl active:scale-95"
                  }`}
                >
                  <span className="text-2xl font-extrabold">
                    {submitting ? "APPLYING..." : "APPLY NOW"}
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        open={showTermsModal}
        onCancel={() => setShowTermsModal(false)}
        footer={null}
        width={700}
        centered
        title={null}
      >
        <div className="flex flex-col max-h-[70vh]">
          <div className="flex border-b border-gray-200 pb-3 mb-4">
            <button
              onClick={() => setActiveTermsTab("overview")}
              className={`flex-1 pb-2 text-xl font-extrabold transition-all border-b-2 text-center ${
                activeTermsTab === "overview"
                  ? "border-green-600 text-green-700"
                  : "border-transparent text-gray-400 hover:text-gray-600"
              }`}
            >
              Facilitator Overview
            </button>
            <button
              onClick={() => setActiveTermsTab("legal")}
              className={`flex-1 pb-2 text-xl font-extrabold transition-all border-b-2 text-center ${
                activeTermsTab === "legal"
                  ? "border-green-600 text-green-700"
                  : "border-transparent text-gray-400 hover:text-gray-600"
              }`}
            >
              Legal Agreement
            </button>
          </div>

          <div className="flex-1 overflow-y-auto pr-2 space-y-6 text-gray-700" style={{ maxHeight: "50vh" }}>
            {activeTermsTab === "overview" ? (
              <div className="space-y-6 text-left">
                <div>
                  <h3 className="text-2xl font-extrabold text-black mb-2">KONNECT FACILITATOR OVERVIEW</h3>
                  <p className="text-lg text-gray-600 leading-relaxed">
                    A Konnect Facilitator is an onboarding powerhouse helping restaurants, wholesalers, and retail stores join and optimize their operations on Konnect. Earn commission from referred business sales and branch activations.
                  </p>
                </div>

                <div className="bg-green-50/50 border border-green-100 rounded-2xl p-5 space-y-3">
                  <h4 className="text-xl font-bold text-green-800 flex items-center gap-2">
                    <span>💸</span> HOW YOU EARN
                  </h4>
                  <p className="text-base">
                    <strong>Sales Formula:</strong> 1.5% from every business sale you facilitate.
                  </p>
                  <p className="text-base">
                    <strong>Milestone Rewards:</strong> Up to ₦12,000 for each verified business branch onboarding.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-6 text-left text-base leading-relaxed text-gray-600">
                <h3 className="text-2xl font-extrabold text-black mb-2">KONNECT FACILITATOR AGREEMENT</h3>

                <p>
                  This Agreement is made between Consukon Limited (owners of Konnect) (“The Company”) and the undersigned Facilitator. The Facilitator agrees to all terms below.
                </p>

                <div>
                  <h4 className="font-extrabold text-black mb-1">1. Status</h4>
                  <p>
                    The Facilitator is an independent contractor. This Agreement does not create employment, partnership, or agency. The Facilitator cannot bind Konnect to any obligation.
                  </p>
                </div>

                <div>
                  <h4 className="font-extrabold text-black mb-1">2. Duties</h4>
                  <p>
                    The Facilitator shall promote Konnect to potential business partners, assist them in setting up their accounts and locations, and represent Konnect professionally at all times.
                  </p>
                </div>

                <div>
                  <h4 className="font-extrabold text-black mb-1">3. Compensation</h4>
                  <p>
                    The Facilitator is entitled to a 1.5% commission on all transactions completed by businesses they facilitate, and up to ₦12,000 per verified branch location.
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 pt-4 mt-4 flex gap-3">
            <button
              onClick={() => {
                setAgreedToTerms(true);
                setShowTermsModal(false);
              }}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-extrabold text-lg py-3 rounded-xl transition"
            >
              I Agree & Accept Terms
            </button>
            <button
              onClick={() => setShowTermsModal(false)}
              className="px-6 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-lg py-3 rounded-xl transition"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={showSuccessModal}
        footer={null}
        closable={false}
        centered
      >
        <div className="flex flex-col items-center px-1 text-center w-full">
          <div className="mb-4 bg-green-50 p-4 rounded-2xl inline-block border border-green-100">
            <span className="text-4xl">🏢</span>
          </div>
          <h2 className="font-gilroyBold text-4xl text-gray-900 mb-6 leading-tight">
            Congratulations! 🎉
          </h2>
          <div className="border border-green-200 rounded-xl p-4 mb-6 bg-green-50/30 w-full">
            <p className="text-gray-900 text-lg">
              You are now a <span className="font-extrabold text-[#16a34a]">Konnect Facilitator</span>!
            </p>
            <p className="text-gray-500 text-lg mt-2">
              Start onboarding businesses and earning weekly commission!
            </p>
          </div>
          <button
            onClick={handleSuccessClose}
            className="bg-green-600 hover:bg-green-700 uppercase text-white flex flex-col items-center justify-center rounded-xl font-gilroyBold w-full transition-all shadow-lg hover:shadow-xl active:scale-95 py-4 px-6 mb-3"
          >
            <span className="text-2xl font-gilroyBold">
              VIEW DASHBOARD
            </span>
          </button>
        </div>
      </Modal>

      <FooterDisplay activeTab="profile" />
    </div>
  );
};

export default JoinAsFacilitator;
